import React, { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../../../firebase/firebase";
import { useLanguage } from "../../../../context/LanguageContext";
import { useAdminSettingsTranslation } from "../translations";
import { updateStoreSettings } from "../../../../firebase/settings-functions";
import "./PaymentTab.css";

const PaymentSettings = ({ storeData, handleChange, storeId, setStoreData }) => {
    const { language } = useLanguage();
    const { t } = useAdminSettingsTranslation(language);

    const [stripeConfig, setStripeConfig] = useState({
        publishableKey: "",
        secretKey: "",
        isConfigured: false,
        source: "environment"
    });

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [savingPaymentMethods, setSavingPaymentMethods] = useState(false);
    const [testing, setTesting] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [paymentMethodsSuccess, setPaymentMethodsSuccess] = useState("");

    useEffect(() => {
        fetchStripeConfig();
    }, [storeId]);

    const fetchStripeConfig = async () => {
        setLoading(true);
        setError("");

        try {
            const getStripeConfiguration = httpsCallable(functions, "getStripeConfiguration");
            const result = await getStripeConfiguration({ storeId });

            if (result.data.success) {
                setStripeConfig(result.data.stripeConfig);
            }
        } catch (err) {
            console.error("Error fetching Stripe config:", err);
            setError(t("failedToLoadStripeConfig") || "Failed to load Stripe configuration");
        } finally {
            setLoading(false);
        }
    };

    const handleStripeChange = (e) => {
        const { name, value } = e.target;
        setStripeConfig({
            ...stripeConfig,
            [name]: value
        });
    };

    const handleSaveStripe = async (e) => {
        e.preventDefault();

        if (!stripeConfig.publishableKey || !stripeConfig.secretKey) {
            setError(t("stripeKeysRequired") || "Publishable and secret keys are required");
            return;
        }

        setSaving(true);
        setError("");
        setSuccess("");

        try {
            const updateStripeConfiguration = httpsCallable(functions, "updateStripeConfiguration");
            const result = await updateStripeConfiguration({
                storeId,
                stripeConfig: {
                    publishableKey: stripeConfig.publishableKey,
                    secretKey: stripeConfig.secretKey
                }
            });

            if (result.data.success) {
                setSuccess(t("stripeConfigSaved") || "Stripe configuration saved successfully!");
                await fetchStripeConfig(); // Refresh to get updated data
            }
        } catch (err) {
            console.error("Error saving Stripe config:", err);
            setError(err.message || (t("failedToSaveStripeConfig") || "Failed to save Stripe configuration"));
        } finally {
            setSaving(false);
        }
    };

    const handleTestStripe = async () => {
        setTesting(true);
        setError("");
        setSuccess("");

        try {
            const testStripeConfiguration = httpsCallable(functions, "testStripeConfiguration");
            const result = await testStripeConfiguration({ storeId });

            if (result.data.success) {
                const { accountInfo } = result.data;
                setSuccess(
                    `✅ ${result.data.message}\n\n` +
                    `Account: ${accountInfo.businessName || accountInfo.email}\n` +
                    `Country: ${accountInfo.country}\n` +
                    `Currency: ${accountInfo.currency}\n` +
                    `Charges: ${accountInfo.chargesEnabled ? 'Enabled' : 'Disabled'}\n` +
                    `Payouts: ${accountInfo.payoutsEnabled ? 'Enabled' : 'Disabled'}`
                );
            }
        } catch (err) {
            console.error("Error testing Stripe config:", err);
            setError(err.message || (t("failedToTestStripeConfig") || "Failed to test Stripe configuration"));
        } finally {
            setTesting(false);
        }
    };

    const handleSavePaymentMethods = async () => {
        if (!storeId) {
            setError("Store ID is not configured.");
            return;
        }

        setSavingPaymentMethods(true);
        setPaymentMethodsSuccess("");
        setError("");

        try {
            await updateStoreSettings(storeId, {
                paymentMethods: storeData.paymentMethods,
                bankTransferDetails: storeData.bankTransferDetails
            });

            setPaymentMethodsSuccess(t('paymentMethodsSaved') || "Payment methods saved successfully!");

            setTimeout(() => {
                setPaymentMethodsSuccess("");
            }, 3000);
        } catch (err) {
            console.error("Error saving payment methods:", err);
            setError(t('failedToSavePaymentMethods') || "Failed to save payment methods: " + err.message);
        } finally {
            setSavingPaymentMethods(false);
        }
    };

    if (loading) {
        return (
            <div className="payment-settings-section">
                <div className="loading-container">
                    <p>{t("loading") || "Loading..."}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="payment-settings-section">
            <h2>{t('paymentSettings')}</h2>

            <div className="payment-methods-container">
                <p className="section-help">{t('acceptedPaymentMethods')}</p>

                <div className="payment-method-item">
                    <input
                        type="checkbox"
                        id="creditCard"
                        name="paymentMethods.creditCard"
                        checked={storeData.paymentMethods.creditCard}
                        onChange={handleChange}
                    />
                    <label htmlFor="creditCard">{t('creditCard')}</label>
                </div>

                {storeData.paymentMethods.creditCard && (
                    <div className="stripe-config-section">
                        <h4 className="subsection-title">{t("stripeConfiguration") || "Stripe Configuration"}</h4>

                        {stripeConfig.isConfigured && (
                            <div className={`config-status ${stripeConfig.source}`}>

                            </div>
                        )}

                        <form onSubmit={handleSaveStripe} className="stripe-config-form">
                            <div className="input-group">
                                <label htmlFor="publishableKey">
                                    {t("stripePublishableKey") || "Publishable Key"} *
                                </label>
                                <input
                                    type="text"
                                    id="publishableKey"
                                    name="publishableKey"
                                    value={stripeConfig.publishableKey}
                                    onChange={handleStripeChange}
                                    placeholder="pk_live_..."
                                    required
                                />
                                <span className="input-hint">
                                    {t("stripePublishableKeyHint") || "Public key that starts with pk_"}
                                </span>
                            </div>

                            <div className="input-group">
                                <label htmlFor="secretKey">
                                    {t("stripeSecretKey") || "Secret Key"} *
                                </label>
                                <input
                                    type="password"
                                    id="secretKey"
                                    name="secretKey"
                                    value={stripeConfig.secretKey}
                                    onChange={handleStripeChange}
                                    placeholder="sk_live_..."
                                    required
                                />
                                <span className="input-hint">
                                    {t("stripeSecretKeyHint") || "Secret key that starts with sk_ (will be encrypted)"}
                                </span>
                            </div>

                            {error && <div className="error-message">{error}</div>}
                            {success && <div className="success-message" style={{ whiteSpace: 'pre-line' }}>{success}</div>}

                            <div className="button-group">
                                <button type="submit" className="btn-primary" disabled={saving}>
                                    {saving ? (t("saving") || "Saving...") : (t("saveStripeConfig") || "Save Stripe Configuration")}
                                </button>
                                <button
                                    type="button"
                                    className="btn-secondary"
                                    onClick={handleTestStripe}
                                    disabled={testing || !stripeConfig.publishableKey || !stripeConfig.secretKey}
                                >
                                    {testing ? (t("testing") || "Testing...") : (t("testStripeConfig") || "Test Configuration")}
                                </button>
                            </div>
                        </form>

                        <div className="stripe-help">
                            <p><strong>{t("whereToFindKeys") || "Where to find your Stripe keys:"}</strong></p>
                            <ol>
                                <li>{t("stripeStep1") || "Go to Stripe Dashboard"}</li>
                                <li>{t("stripeStep2") || "Navigate to Developers → API keys"}</li>
                                <li>{t("stripeStep3") || "Copy your Publishable key and Secret key"}</li>
                            </ol>
                            <p className="security-note">
                                🔒 {t("stripeSecurityNote") || "Your secret key is encrypted before storage for security"}
                            </p>
                        </div>
                    </div>
                )}

                <div className="payment-method-item">
                    <input
                        type="checkbox"
                        id="cashOnDelivery"
                        name="paymentMethods.cashOnDelivery"
                        checked={storeData.paymentMethods.cashOnDelivery}
                        onChange={handleChange}
                    />
                    <label htmlFor="cashOnDelivery">{t('cashOnDelivery')}</label>
                </div>

                <div className="payment-method-item">
                    <input
                        type="checkbox"
                        id="manualTransfer"
                        name="paymentMethods.manualTransfer"
                        checked={storeData.paymentMethods.manualTransfer}
                        onChange={handleChange}
                    />
                    <label htmlFor="manualTransfer">{t('manualTransfer') || 'Manual Bank Transfer'}</label>
                </div>

                {storeData.paymentMethods.manualTransfer && (
                    <div className="bank-transfer-details-section">
                        <h4 className="subsection-title">{t("bankTransferDetails") || "Bank Transfer Details"}</h4>
                        <p className="section-help">
                            {t("bankTransferDetailsNote") || "Paste your bank details here. This information will be shown to customers who select manual transfer payment."}
                        </p>

                        <div className="language-tabs">
                            <button
                                type="button"
                                className={`lang-tab ${language === 'en' ? 'active' : ''}`}
                                onClick={() => { }}
                            >
                                English
                            </button>
                            <button
                                type="button"
                                className={`lang-tab ${language === 'ar' ? 'active' : ''}`}
                                onClick={() => { }}
                            >
                                العربية
                            </button>
                        </div>

                        <div className="form-group">
                            <label htmlFor="bankDetailsEn">
                                {t("bankDetailsEnglish") || "Bank Details (English)"}
                            </label>
                            <textarea
                                id="bankDetailsEn"
                                rows="6"
                                value={storeData.bankTransferDetails?.en || ''}
                                onChange={(e) => {
                                    handleChange({
                                        target: {
                                            name: 'bankTransferDetails',
                                            value: {
                                                ...storeData.bankTransferDetails,
                                                en: e.target.value
                                            }
                                        }
                                    });
                                }}
                                placeholder="Example:&#10;Bank Name: ABC Bank&#10;Account Name: Your Business Name&#10;Account Number: 1234567890&#10;IBAN: AE000000000000000000000&#10;SWIFT Code: ABCAAEXX"
                                dir="ltr"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="bankDetailsAr">
                                {t("bankDetailsArabic") || "Bank Details (Arabic)"}
                            </label>
                            <textarea
                                id="bankDetailsAr"
                                rows="6"
                                value={storeData.bankTransferDetails?.ar || ''}
                                onChange={(e) => {
                                    handleChange({
                                        target: {
                                            name: 'bankTransferDetails',
                                            value: {
                                                ...storeData.bankTransferDetails,
                                                ar: e.target.value
                                            }
                                        }
                                    });
                                }}
                                placeholder="مثال:&#10;اسم البنك: بنك ABC&#10;اسم الحساب: اسم نشاطك التجاري&#10;رقم الحساب: 1234567890&#10;رقم الآيبان: AE000000000000000000000&#10;رمز السويفت: ABCAAEXX"
                                dir="rtl"
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* Save Payment Methods Button */}
            <div className="settings-form-actions" style={{ marginTop: '30px' }}>
                {paymentMethodsSuccess && <div className="admin-success-message">{paymentMethodsSuccess}</div>}
                {error && <div className="admin-error-message">{error}</div>}
                <button
                    type="button"
                    className="admin-primary-btn save-settings-btn"
                    onClick={handleSavePaymentMethods}
                    disabled={savingPaymentMethods}
                >
                    {savingPaymentMethods ? (t('saving') || 'Saving...') : (t('savePaymentMethods') || 'Save Payment Methods')}
                </button>
            </div>
        </div>
    );
};

export default PaymentSettings;